import type { VoiceStatus } from "@/store/assistant-voice";
import {
	VOICE_FLAGS_OFF,
	type VoiceExperimentFlags,
} from "@/types/ai-assistant-style";
import { startVoiceSession } from "./voice.api";
import { createBargeInGate } from "./voice-barge-in";
import { createBreathPlayer } from "./voice-breath-player";
import {
	DELEGATION_FALLBACK_MS,
	FALLBACK_QUIET_MS,
	MIC_OPTIONS,
	num,
	OUTPUT_DELTA_SAMPLES,
	PANEL_SETTLE,
	REMOTE_AUDIBLE_RMS,
	STARTED_FALLBACK_MS,
	WAITING_HINT_MS,
} from "./voice-controller.config";
import type {
	VoiceController,
	VoiceControllerOptions,
} from "./voice-controller.types";
import {
	createLevelMeter,
	type LevelMeter,
	openMic,
	stopStream,
} from "./voice-devices";
import { createFillerTracker } from "./voice-filler";
import { openPeer, type Peer } from "./voice-peer";
import {
	commentaryEvent,
	instructionsAppendEvent,
} from "./voice-peer-messages";
import { createSettleWatcher } from "./voice-settle";
import { createUnhandledEventLog, logVoiceStage } from "./voice-stage-log";
import { describeDeltaShape } from "./voice-transcript";
import { createTurnRunner } from "./voice-turn";
import { createWaitSound } from "./voice-wait-sound";
import { createWallMic } from "./voice-wall-mic";

export type {
	VoiceAnswer,
	VoiceController,
	VoiceControllerEvents,
	VoiceControllerOptions,
} from "./voice-controller.types";

/**
 * Sesi suara panel: mikrofon → GPT-Live (WebRTC). Tiap delegasi ditanyakan ke
 * Claude lalu jawabannya dikirim per kalimat (`session.commentary.append`).
 * Ucapan bermakna saat menjawab = barge-in (lihat `voice-barge-in.ts`); saat
 * menunggu GPT-Live mengucapkan kata pengisi; waktu tiap tahap dilaporkan.
 */

/** Buka mikrofon & sesi GPT-Live, lalu jalankan giliran tanya-jawab suara. */
export async function createVoiceController(
	opts: VoiceControllerOptions,
): Promise<VoiceController> {
	const { events } = opts;
	// Dibuat di klik On supaya browser mengizinkan AudioContext bunyi tunggu.
	const breath = createBreathPlayer();
	breath.prime();
	const mic = await openMic(MIC_OPTIONS).catch((err: unknown) => {
		breath.close();
		throw err;
	});
	const remote = new Audio();
	const wallMic = opts.wall
		? createWallMic({
				mic,
				remote,
				config: opts.wall,
				onBargeIn: () => turns.bargeIn(),
				onState: (state) => events.wallMic?.(state),
			})
		: null;
	const settle = createSettleWatcher(() => performance.now(), PANEL_SETTLE);
	let peer: Peer | null = null;
	let meter: LevelMeter | null = null;
	let closed = false;
	let started = false;
	const gate = createBargeInGate();
	const waitSound = createWaitSound({
		player: breath,
		onActive: (active, at) => gate.waitSound(active, at),
		now: () => performance.now(),
	});
	let fillerInstruction = "";
	let flags: VoiceExperimentFlags = VOICE_FLAGS_OFF;
	let lastAudibleAt = Number.NEGATIVE_INFINITY;
	let fallbackTimer: ReturnType<typeof setTimeout> | null = null;
	let waitingHintTimer: ReturnType<typeof setTimeout> | null = null;
	/** Potongan transkrip Jenna yang bentuknya sudah dicatat pada ucapan user ini. */
	let deltaSamples = 0;
	const filler = createFillerTracker((text) => {
		if (!closed) events.filler(text);
	});
	const logUnhandledEvent = createUnhandledEventLog();

	const setStatus = (s: VoiceStatus) => {
		if (!closed) events.status(s);
	};

	const turns = createTurnRunner({
		events,
		messages: opts,
		gate,
		waitSound,
		filler,
		settle,
		commentary: (delegationId, content) =>
			peer?.send(commentaryEvent(delegationId, content)),
		setStatus,
		isClosed: () => closed,
		log: logVoiceStage,
	});

	const cancelWaitingHint = () => {
		if (waitingHintTimer) clearTimeout(waitingHintTimer);
		waitingHintTimer = null;
	};

	const cancelFallback = () => {
		if (fallbackTimer) clearTimeout(fallbackTimer);
		fallbackTimer = null;
	};

	/**
	 * Setelah pengguna berhenti bicara, beri tahu bahwa pertanyaannya sedang
	 * dikerjakan. Tanpa ini chip tetap "Mendengarkan…" selama penantian delegasi
	 * — pengguna mengira ucapannya tidak tertangkap, lalu bicara lagi, dan ucapan
	 * itu membatalkan gilirannya sendiri lewat `turns.bargeIn()`.
	 *
	 * Hanya dipasang selama masih ADA penantian (`fallbackTimer`); begitu
	 * delegasi tiba, `cancelFallback` mematikannya sehingga jalur sehat tidak
	 * pernah menampilkan penanda ini.
	 */
	const armWaitingHint = () => {
		cancelWaitingHint();
		waitingHintTimer = setTimeout(() => {
			waitingHintTimer = null;
			if (closed || !fallbackTimer) return;
			setStatus("thinking");
		}, WAITING_HINT_MS);
	};

	/**
	 * Delegasi tak kunjung datang setelah ucapan user: tanya Claude sendiri (teks saja).
	 *
	 * JANGAN perpendek penjadwalan ulang di bawah tanpa menaikkan anggaran waktu.
	 *
	 * Saat timer jatuh tempo tetapi Jenna masih bersuara, baris `return
	 * scheduleFallback()` mengulang `DELEGATION_FALLBACK_MS` PENUH (6 detik),
	 * bukan mengecek lagi sebentar kemudian. Itu tampak seperti kelalaian dan
	 * menggoda untuk "dirapikan" jadi interval pendek — tetapi pengulangan penuh
	 * itulah yang menahan beban.
	 *
	 * Dari satu sesi uji yang terukur, latensi delegasi GPT-Live: 3,2 / 4,7 /
	 * 11,0 detik — ambangnya 6 detik. Giliran 11 detik itu HANYA selamat karena
	 * pengulangan penuh memundurkan jatuh tempo dari ~36,4 s ke ~42,4 s,
	 * sementara delegasinya tiba di 41,4 s. Marginnya ~1,0 detik. Dengan selang
	 * pendek (mis. cek tiap 500 ms), giliran itu jatuh ke fallback di detik ~38
	 * dan pengguna melihat pesan oranye.
	 *
	 * Jadi memperbaiki penjadwalan ulang SENDIRIAN membuat gejalanya LEBIH
	 * SERING muncul, bukan lebih jarang. Ia harus dikerjakan bersama kenaikan
	 * anggaran waktu (rancangan: anggaran dasar ~8 s, dan ~20 s begitu kalimat
	 * tunggu Jenna terdeteksi), tidak boleh sepotong.
	 *
	 * Catatan: sejak tampilan uji suara dicabut, tidak ada lagi cara membaca log
	 * tahap dari tab pengguna — kalau keseimbangan ini dirusak, tidak ada
	 * instrumen yang akan memberi tahu. Komentar ini satu-satunya penjaga yang
	 * ikut berpindah bersama repo.
	 */
	const scheduleFallback = () => {
		if (!fallbackTimer) logVoiceStage("fallback_scheduled");
		cancelFallback();
		fallbackTimer = setTimeout(() => {
			fallbackTimer = null;
			if (closed) return;
			// Mengulang anggaran PENUH — disengaja, lihat docstring di atas.
			if (performance.now() - lastAudibleAt < FALLBACK_QUIET_MS)
				return scheduleFallback();
			void turns.fallback();
		}, DELEGATION_FALLBACK_MS);
	};

	/** Sekali tepat setelah `session.started`: append bisa memotong ucapan yang berjalan. */
	const appendFillerInstruction = () => {
		const event = instructionsAppendEvent(fillerInstruction);
		if (event) peer?.send(event);
	};

	const onEvent = (event: Record<string, unknown>) => {
		const type = String(event.type ?? "");
		if (type === "session.started") {
			started = true;
			appendFillerInstruction();
			setStatus("ready");
		} else if (type === "session.input_transcript.delta") {
			const firstOfUtterance = !gate.text();
			if (firstOfUtterance) deltaSamples = 0;
			gate.input(
				String(event.delta ?? ""),
				performance.now(),
				num(event.start_ms),
				num(event.end_ms),
			);
			const ignored = gate.verdict(turns.phase()) === "ignore";
			if (firstOfUtterance)
				logVoiceStage("speech_detected", ignored ? "echo" : undefined);
			if (ignored) return;
			turns.bargeIn();
			scheduleFallback();
			armWaitingHint();
			if (flags.filler) filler.open();
			events.transcript(gate.text());
			events.activity();
			setStatus("listening");
		} else if (type === "session.output_transcript.delta") {
			const delta = String(event.delta ?? "");
			if (deltaSamples < OUTPUT_DELTA_SAMPLES) {
				deltaSamples++;
				logVoiceStage("output_delta", describeDeltaShape(delta));
			}
			gate.spoke(delta);
			filler.output(delta);
			events.activity();
		} else if (type === "session.delegation.created") {
			const id = (event.delegation as { id?: unknown } | undefined)?.id;
			if (fallbackTimer) logVoiceStage("fallback_cancelled", "delegation");
			cancelFallback();
			cancelWaitingHint();
			if (typeof id === "string") void turns.delegate(id);
		} else if (type === "session.closed") {
			if (!closed) {
				logVoiceStage("session_lost", "session.closed");
				events.lost();
			}
		} else {
			// Tipe di luar daftar di atas dulunya dibuang tanpa jejak, sehingga
			// delegasi yang datang dengan nama lain tidak bisa dibedakan dari
			// delegasi yang memang tidak pernah dikirim.
			logUnhandledEvent(type);
		}
	};

	const stop = () => {
		if (closed) return;
		closed = true;
		cancelFallback();
		turns.stop();
		settle.stopAll();
		waitSound.close();
		meter?.stop();
		wallMic?.stop();
		remote.srcObject = null;
		peer?.close();
		stopStream(mic);
	};

	try {
		peer = await openPeer({
			mic,
			exchange: async (offer) => {
				const start = await startVoiceSession(offer);
				fillerInstruction = start.fillerInstruction;
				flags = start.flags;
				gate.setEchoFilter(flags.echoFilter);
				waitSound.configure(start.waitSound);
				gate.setFillerPhrases(start.fillerPhrases);
				events.session(start);
				return start.sdp;
			},
			onEvent,
			onClose: () => {
				if (!closed) {
					logVoiceStage("session_lost", "peer");
					events.lost();
				}
			},
			onRemoteStream: (stream) => {
				remote.srcObject = stream;
				// Autoplay diblokir jarang terjadi (diawali klik user); jawaban tetap tampil sebagai teks.
				remote.play().catch(() => events.audioBlocked());
				meter = createLevelMeter(stream, (rms, now) => {
					if (rms <= REMOTE_AUDIBLE_RMS) return;
					lastAudibleAt = now;
					settle.heard(now);
					gate.audible(now);
					turns.audio(now);
					waitSound.audible();
					wallMic?.audible();
				});
			},
		});
	} catch (err) {
		stop();
		throw err;
	}

	setTimeout(() => {
		if (!started) setStatus("ready");
	}, STARTED_FALLBACK_MS);

	return {
		setAnswerMuted(muted) {
			remote.muted = muted;
			waitSound.setMuted(muted);
		},
		setMicMuted(muted) {
			if (wallMic) return wallMic.setUserMuted(muted);
			for (const track of mic.getAudioTracks()) track.enabled = !muted;
		},
		pttPress: () => wallMic?.press(),
		pttRelease: () => wallMic?.release(),
		stop,
	};
}
